// Cross-Module State Sync - state synchronization logic extracted from CrossModuleIntegration
// ✅ NO MAGIC STRINGS: Use typed enums and constants

import { IntegrationEvents } from "../constants/Events";
import { EventBus } from "../events/EventBus";
import debugLogger from "../utils/debugLogger";

import {
  ModuleDependency,
  IntegrationConfig,
  IntegrationMetrics,
  IntegrationError,
  IntegrationErrorType,
  ModuleState,
  StateSyncRequest,
  StateSyncResponse,
} from "./types";
import { ModuleHealthStatus } from "./types";

/**
 * Shared mutable state passed in from the CrossModuleIntegration facade.
 * The state sync module mutates these maps directly to preserve behavior.
 */
export interface StateSyncState {
  eventBus: EventBus;
  config: IntegrationConfig;
  metrics: IntegrationMetrics;
  moduleDependencies: Map<string, ModuleDependency[]>;
  moduleStates: Map<string, ModuleState>;
}

/**
 * State synchronizer: manages module state, dependencies, and state sync
 * request/response flow. Mutates the shared state passed in.
 */
export class CrossModuleStateSync {
  constructor(private state: StateSyncState) {}

  /**
   * Register module dependencies with state tracking
   */
  public registerModuleDependency(
    moduleName: string,
    dependency: ModuleDependency,
  ): void {
    const { state } = this;

    if (!state.moduleDependencies.has(moduleName)) {
      state.moduleDependencies.set(moduleName, []);
    }
    state.moduleDependencies.get(moduleName)!.push(dependency);

    // Initialize module state if not exists
    if (!state.moduleStates.has(moduleName)) {
      state.moduleStates.set(moduleName, {
        moduleName,
        status: "active",
        lastActivity: new Date(),
        dependencies: [],
        interactions: [],
        healthStatus: ModuleHealthStatus.HEALTHY,
        performanceMetrics: {
          averageLatency: 0,
          totalEvents: 0,
          errorRate: 0,
          lastUpdated: new Date(),
        },
      });
    }

    state.metrics.dependencyResolutions++;
    debugLogger.info(
      "integration",
      `Module dependency registered with state tracking: ${moduleName} -> ${dependency.targetModule}`,
    );
  }

  /**
   * Synchronize module states across the system
   */
  public async synchronizeModuleStates(): Promise<void> {
    const { state } = this;

    if (!state.config.enableStateSynchronization) {
      return;
    }

    try {
      const syncRequest: StateSyncRequest = {
        requestId: this.generateEventId(),
        timestamp: new Date().toISOString(),
        requestingModule: "integration",
        targetModules: Array.from(state.moduleStates.keys()),
      };

      state.metrics.stateSyncRequests++;

      // Emit state sync request
      state.eventBus.emitGlobal(
        IntegrationEvents.STATE_SYNC_REQUESTED,
        syncRequest,
      );

      // Wait for responses with timeout
      const responses = await this.waitForStateSyncResponses(
        syncRequest.requestId,
      );

      // Update module states
      responses.forEach((response) => {
        state.moduleStates.set(response.moduleName, {
          ...response.state,
          lastActivity: new Date(),
        });
      });

      state.metrics.stateSyncResponses += responses.length;
      debugLogger.info(
        "integration",
        `Module states synchronized: ${responses.length} modules updated`,
      );
    } catch (error) {
      debugLogger.error(
        "integration",
        "Failed to synchronize module states",
        error,
      );
      throw new IntegrationError(
        IntegrationErrorType.STATE_SYNC_FAILED,
        "State synchronization failed",
      );
    }
  }

  /**
   * Get module state information
   */
  public getModuleState(moduleName: string): ModuleState | null {
    return this.state.moduleStates.get(moduleName) || null;
  }

  /**
   * Get all module states
   */
  public getAllModuleStates(): ModuleState[] {
    return Array.from(this.state.moduleStates.values());
  }

  /**
   * Update module state
   */
  public updateModuleState(
    moduleName: string,
    updates: Partial<ModuleState>,
  ): void {
    const { state } = this;
    const currentState = state.moduleStates.get(moduleName);
    if (currentState) {
      state.moduleStates.set(moduleName, {
        ...currentState,
        ...updates,
        lastActivity: new Date(),
      });
    }
  }

  /**
   * Get module dependencies
   */
  public getModuleDependencies(moduleName: string): ModuleDependency[] {
    return this.state.moduleDependencies.get(moduleName) || [];
  }

  /**
   * Initialize module state for a newly registered module.
   */
  public handleModuleRegistered(moduleName: string): void {
    const { state } = this;
    state.moduleStates.set(moduleName, {
      moduleName,
      status: "active",
      lastActivity: new Date(),
      dependencies: [],
      interactions: [],
      healthStatus: ModuleHealthStatus.HEALTHY,
      performanceMetrics: {
        averageLatency: 0,
        totalEvents: 0,
        errorRate: 0,
        lastUpdated: new Date(),
      },
    });
  }

  /**
   * Remove module state when a module is unregistered.
   */
  public handleModuleUnregistered(moduleName: string): void {
    const { state } = this;
    state.moduleStates.delete(moduleName);
  }

  private generateEventId(): string {
    return `event_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private async waitForStateSyncResponses(
    _requestId: string,
  ): Promise<StateSyncResponse[]> {
    const { state } = this;
    return new Promise((resolve) => {
      const responses: StateSyncResponse[] = [];
      const timeout = setTimeout(() => {
        resolve(responses);
      }, state.config.eventTimeout);

      // This would need to be implemented with proper event listening
      // For now, we'll return an empty array
      setTimeout(() => {
        clearTimeout(timeout);
        resolve(responses);
      }, 100);
    });
  }

  private resolveStateConflicts(
    existingState: ModuleState | undefined,
    newState: ModuleState,
  ): ModuleState {
    if (!existingState) {
      return newState;
    }

    // Simple conflict resolution - prefer newer state
    return {
      ...existingState,
      ...newState,
      lastActivity: new Date(),
    };
  }
}
